import { Article } from 'src/app/interfaces/article';
import { readableParagraphsFromEditorPayload } from 'src/app/services/geo-readable-text.util';
import { formatSeoTitle } from 'src/app/services/seo.defaults';
import { buildSeoImageProxyUrl, pickSeoFileInventoryId } from 'src/app/services/seo-image.util';
import { buildArticleJsonLd } from 'src/app/services/seo-json-ld.util';
import { PageSeo } from 'src/app/services/seo.model';
import { SeoService } from 'src/app/services/seo.service';
import { readerEditorPayload, resolveReaderArticleVersion } from './article-display.util';

const ARTICLE_BODY_MAX = 100_000;

const DESC_MAX = 320;

function stripForMeta(s: string | null | undefined, max: number): string {
  if (!s) {
    return '';
  }
  const plain = s
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (plain.length <= max) {
    return plain;
  }
  return `${plain.slice(0, max - 1).trim()}…`;
}

/** Prose from the reader version, for the SSR shell and `articleBody`. */
export function articleReadableParagraphs(article: Article): string[] {
  const version = resolveReaderArticleVersion(article.currentVersions);
  return readableParagraphsFromEditorPayload(readerEditorPayload(version));
}

function sameText(left: string, right: string): boolean {
  return left.replace(/\s+/g, ' ').trim().toLowerCase() === right.replace(/\s+/g, ' ').trim().toLowerCase();
}

/** Shared SEO payload for article detail (client navigation, SSR resolver). */
export function buildArticlePageSeo(article: Article): Partial<PageSeo> {
  const titleVersion = resolveReaderArticleVersion(article.currentVersions);
  const articleTitle = titleVersion?.title ?? 'Article';
  const fullSynopsis = stripForMeta(titleVersion?.synopsis ?? null, ARTICLE_BODY_MAX);
  const synopsisText = stripForMeta(titleVersion?.synopsis ?? null, DESC_MAX);
  const articleBody = [
    fullSynopsis,
    ...articleReadableParagraphs(article).filter((paragraph) => !sameText(paragraph, fullSynopsis)),
  ]
    .filter(Boolean)
    .join('\n\n')
    .slice(0, ARTICLE_BODY_MAX);
  const desc =
    synopsisText || `Read “${articleTitle}” on ChainMart — blockchain learning and insights.`;
  const publishedAtRaw = article.publishedAt
    ? new Date(article.publishedAt as Date | string)
    : null;
  const publishedIso =
    publishedAtRaw && !Number.isNaN(publishedAtRaw.getTime())
      ? publishedAtRaw.toISOString()
      : new Date().toISOString();

  const authorName = article.author?.fullName?.trim() || 'Admin';
  const imageFileInventoryId = pickSeoFileInventoryId(article.thumbnailFileInventory);
  const imageUrl = imageFileInventoryId ? buildSeoImageProxyUrl(imageFileInventoryId) : undefined;

  return {
    title: formatSeoTitle(articleTitle),
    description: desc,
    ogDescription: desc,
    imageUrl,
    imageFileInventoryId,
    ogType: 'article',
    articleAuthor: authorName,
    articlePublishedTime: publishedIso,
    jsonLd: buildArticleJsonLd({
      headline: articleTitle,
      description: desc,
      articleBody: articleBody || undefined,
      datePublished: publishedIso,
      authorName,
      imageUrl,
      url: '',
    }),
  };
}

export function applyArticlePageSeo(seoService: SeoService, article: Article): void {
  seoService.applyPageOverride(buildArticlePageSeo(article));
}
